import type { ObjectType } from '@/@types/graphql-types';
import type { SubscriptionInfo } from '@/interfaces/subscriptions';
import { SUBSCRIPTIONS } from '@/constants/subscriptions.constant';
import type VueRouter from 'vue-router';
import type { Route } from 'vue-router';
import type { LastVistedTitleDetails } from '@/stores/pinia/useTitleDetailsStore';

import { ProModal } from '@/lazy-load-components';

import { WebLocale } from '@/enums/web-locale';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { VFMHelper } from '@/helpers/vfm/vfm-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { SnowplowTitleContext } from '@/helpers/tracking/providers';

const EXPERIMENT_PRO_ONBOARDING_COUNTRIES = [
	WebLocale.be,
	WebLocale.es,
	WebLocale.fr,
	WebLocale.ie,
	WebLocale.nl,
	WebLocale.pt,
	WebLocale.us,
	WebLocale.fi,
	WebLocale.se,
];

export function findSubscriptionBy(key: keyof SubscriptionInfo, value: string | null) {
	return Object.values(SUBSCRIPTIONS).find(subscription => subscription[key] == value);
}

const SELECTED_SUBSCRIPTION = 'selected_subscription' as const;

export function saveSelectedSubscriptionPriceId(subscriptionPriceId: string) {
	localStorage.setItem(SELECTED_SUBSCRIPTION, subscriptionPriceId);
}

export function removeSelectedSubscriptionPriceId() {
	localStorage.removeItem(SELECTED_SUBSCRIPTION);
}

export function getSelectedSubscriptionPriceId() {
	return localStorage.getItem(SELECTED_SUBSCRIPTION);
}

export function isProWebCountry(webLocale: WebLocale) {
	const activeCountries = [
		WebLocale.us,
		WebLocale.de,
		WebLocale.at,
		WebLocale.ie,
		WebLocale.fr,
		WebLocale.es,
		WebLocale.it,
		WebLocale.pl,
		WebLocale.nl,
		WebLocale.se,
		WebLocale.fi,
		WebLocale.be,
		WebLocale.pt,
		WebLocale.cz,
		WebLocale.ro,
		WebLocale.uk,
		WebLocale.au,
	];
	return activeCountries.includes(webLocale);
}

export function isProOnboardingExperimentEnabled(webLocale: WebLocale) {
	return EXPERIMENT_PRO_ONBOARDING_COUNTRIES.includes(webLocale) && isProWebCountry(webLocale);
}

export function priceFormatter(locale: string, price: number, currency: string = 'EUR'): string {
	const formattedLocale = locale.replace(/_/g, '-');

	if (currency === 'AUD' || currency === 'CAD') {
		// For AUD and CAD, we need to use the locale of the country
		const numberPart = new Intl.NumberFormat(formattedLocale, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(price);
		if (currency === 'AUD') {
			return `A$ ${numberPart}`;
		} else if (currency === 'CAD') {
			return `CA$ ${numberPart}`;
		}
	}

	return new Intl.NumberFormat(formattedLocale, { style: 'currency', currency: currency }).format(price);
}
function isPremiumUser(): boolean {
	const userStore = useUserStore();

	return userStore.isPremium;
}

const LAST_PRO_VIEW = 'last_pro_view';

export function saveLastProView(view: string) {
	localStorage.setItem(LAST_PRO_VIEW, view);
}

export function getLastProView() {
	return localStorage.getItem(LAST_PRO_VIEW);
}

const LAST_VIEWED_TITLE = 'last_viewed_title';

interface StoredLastTitleDetails {
	titleObjectId: number;
	titleObjectType: ObjectType;
}

export function saveLastViewedTitleDetail({ titleObjectId, titleObjectType }: LastVistedTitleDetails) {
	if (titleObjectId == null || titleObjectType == null) return;

	const titleInfo: StoredLastTitleDetails = { titleObjectId, titleObjectType };

	localStorage.setItem(LAST_VIEWED_TITLE, JSON.stringify(titleInfo));
}

function getLastViewedTitleDetail() {
	return JSON.parse(localStorage.getItem(LAST_VIEWED_TITLE) ?? 'null') as StoredLastTitleDetails | null;
}

function removeLastViewedTitleDetail() {
	localStorage.removeItem(LAST_VIEWED_TITLE);
}

export function showProFeatures(webLocale: WebLocale): boolean {
	return isProWebCountry(webLocale) || isPremiumUser();
}

export function handleStripeRedirect(
	route: Route,
	router: VueRouter,
	jwLoginId: string | undefined,
	skipRedirect = false
) {
	if (route.query['jw-pro'] === 'redirect') {
		if (!skipRedirect) {
			router.push({ name: 'app.titles.popular.list' });
		}

		ModalHelper.openModal(ProModal, {}, { cssClass: 'pro-jw-pro-modal' }, 'StripeReturn', true);

		saveLastProView('StripeReturn');
	} else if (route.query['jw-pro'] === 'success') {
		const sessionId = route.query['session-id'];

		if (!skipRedirect) {
			router.push({ name: 'app.titles.popular.list' });
		}

		ModalHelper.openModal(ProModal, { modalType: 'loading' }, undefined, undefined, true);
		if (typeof sessionId === 'string' && jwLoginId) {
			handleStripe(jwLoginId);
		} else {
			console.error('missing or multiple session-id query params after Stripe redirect');
		}
	}
}

async function handleStripe(jwLoginId: string) {
	const contexts: SnowplowTitleContext[] = [];
	const title = getLastViewedTitleDetail();
	if (title != null) {
		contexts.push(new SnowplowTitleContext(title.titleObjectId, title.titleObjectType));
	}

	setTimeout(async () => {
		const userStore = useUserStore();
		await userStore.updateUserPremiumStatus();
		const isPremium = userStore.isPremium;

		if (isPremium) {
			await VFMHelper.close();
			ModalHelper.openModal(ProModal, { modalType: 'success' }, undefined, undefined, true);

			TrackingHelper.trackEvent(
				'user_account',
				{
					action: 'subscription_signup',
					label: jwLoginId,
					property: getLastProView() || undefined,
				},
				contexts
			);

			setTimeout(() => {
				const url = new URL(window.location.href);
				url.search = ''; // Remove query parameters
				removeLastViewedTitleDetail();
				window.location.href = url.toString();
			}, 3000);
		} else {
			await VFMHelper.close();
			ModalHelper.openModal(ProModal, { modalType: 'error' }, undefined, undefined, true);

			TrackingHelper.trackEvent('user_account', {
				action: 'subscription_signup_error',
				label: jwLoginId,
				property: getLastProView() || undefined,
			});
		}
	}, 3000);
}
